import {useStorage, useStorageEffect} from "@core-ui/storage-helper";
import dayjs from "dayjs";
import type {BopisStorageType} from "../types/bopis";
import hasLocationExpired from "../util/has-location-expired";

const DEFAULT_EXPIRATION_MINUTES = 30;

type BopisStorageOptionsType = {
  expirationMinutes: number;
  globalScope: Window;
};

export type BopisStorageInterfaceType = {
  getData: () => Omit<BopisStorageType, "expiration">;
  setData: (data: Omit<BopisStorageType, "expiration">) => void;
  update: (data: Partial<Omit<BopisStorageType, "expiration">>) => void;
  set: <T extends keyof BopisStorageType>(
    key: T,
    value: BopisStorageType[T]
  ) => void;
  get: <T extends keyof BopisStorageType>(
    key: T
  ) => BopisStorageType[T] | undefined;
  hasExpired: () => boolean;
  clear: () => void | never;
};

export const useBopisStorage = (
  options?: BopisStorageOptionsType
): BopisStorageInterfaceType => {
  let expirationMinutes = DEFAULT_EXPIRATION_MINUTES;
  if (options && options.expirationMinutes) {
    expirationMinutes = options.expirationMinutes;
  }

  let globalThis;

  if (options && options.globalScope) {
    globalThis = options.globalScope;
  } else {
    globalThis = typeof window !== "undefined" ? window : null;
  }

  // TODO: consider pass error logger into useStorage hook
  const storage = useStorage<BopisStorageType>("bopis", {
    jsonify: true,
    globalScope: globalThis,
  });

  const {getData, setData, update, set, get, clear} = storage;

  return {
    getData: () => {
      const {expiration, ...data} = getData();
      return data;
    },
    get,
    setData: (data: Omit<BopisStorageType, "expiration">): void => {
      if (Object.prototype.hasOwnProperty.call(data, "expiration")) {
        throw Error("expiration key is readonly and cannot be edited");
      }

      const expiration = dayjs().add(expirationMinutes, "minutes").toDate();
      setData({
        ...data,
        expiration,
      });
    },
    set: <T extends keyof BopisStorageType>(
      key: T,
      value: BopisStorageType[T]
    ): void => {
      if (key === "expiration") {
        throw Error("expiration key is readonly and cannot be edited");
      }

      if (key === "stores") {
        const expiration = dayjs().add(expirationMinutes, "minutes").toDate();
        set("expiration", expiration);
      }

      set(key, value);
    },
    update: (data: Partial<Omit<BopisStorageType, "expiration">>): void => {
      const updateData = data as Partial<BopisStorageType>;

      if (Object.prototype.hasOwnProperty.call(updateData, "expiration")) {
        throw Error("expiration key is readonly and cannot be edited");
      }

      if (Object.prototype.hasOwnProperty.call(updateData, "stores")) {
        const expiration = dayjs().add(expirationMinutes, "minutes").toDate();
        updateData.expiration = expiration;
      }

      update(updateData);
    },
    hasExpired: () => hasLocationExpired(getData()),
    clear,
  };
};

export const useBopisStorageEffect = (
  effect: (
    prev: BopisStorageType | null,
    current: BopisStorageType | null
  ) => void
) => {
  const globalScope = typeof window !== "undefined" ? window : undefined;

  useStorageEffect(
    (key, prev, current) => {
      if (key === "bopis" && prev !== current) {
        let oldValueParsed = null;
        let newValueParsed = null;
        if (prev) oldValueParsed = JSON.parse(prev);
        if (current) newValueParsed = JSON.parse(current);
        effect(oldValueParsed, newValueParsed);
      }
    },
    ["bopis"],
    globalScope
  );
};
